/* IdealofTech theme.js — cart drawer, product form, filters, compare, wizards, form validation */
(() => {
  'use strict';
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const R = window.theme?.routes || { cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', root: '/' };
  const root = (R.root || '/').replace(/\/?$/, '/');
  const announce = msg => { const el = $('#a11y-status'); if (el) { el.textContent = ''; setTimeout(() => (el.textContent = msg), 40); } };
  const toast = msg => { const t = document.createElement('div'); t.className = 'toast'; t.setAttribute('role', 'status'); t.textContent = msg; document.body.appendChild(t); setTimeout(() => t.remove(), 3800); };
  const store = { get(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; } }, set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} } };
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- Focus trap helper ---------- */
  function trap(container, onClose) {
    const prev = document.activeElement;
    const focusables = () => $$('a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select,textarea,[tabindex]:not([tabindex="-1"])', container).filter(el => el.offsetParent !== null);
    const key = e => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key !== 'Tab') return;
      const f = focusables(); if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('keydown', key); if (prev && prev.focus) prev.focus(); };
  }

  /* ---------- Mobile menu ---------- */
  let releaseMenu = null;
  document.addEventListener('click', e => {
    const open = e.target.closest('[data-menu-open]'), close = e.target.closest('[data-menu-close]');
    const menu = $('#MobileMenu'); if (!menu) return;
    if (open) { menu.hidden = false; document.body.style.overflow = 'hidden'; open.setAttribute('aria-expanded', 'true'); $('a,button', menu)?.focus(); releaseMenu = trap(menu, closeMenu); }
    if (close) closeMenu();
    function closeMenu() { menu.hidden = true; document.body.style.overflow = ''; $$('[data-menu-open]').forEach(b => b.setAttribute('aria-expanded', 'false')); if (releaseMenu) { releaseMenu(); releaseMenu = null; } }
  });

  /* ---------- Cart drawer ---------- */
  const drawerEnabled = () => !!$('[data-cart-drawer]');
  let releaseDrawer = null;
  async function refreshDrawer() {
    try {
      const res = await fetch(`${root}?sections=cart-drawer`);
      const data = await res.json();
      const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const fresh = $('[data-cart-drawer]', html); const cur = $('[data-cart-drawer]');
      if (fresh && cur) { const wasOpen = !cur.hidden; cur.innerHTML = fresh.innerHTML; cur.hidden = !wasOpen; }
      const cart = await (await fetch(`${R.cart}.js`)).json();
      $$('[data-cart-count]').forEach(el => (el.textContent = cart.item_count));
      $$('[data-cart-open]').forEach(el => el.setAttribute('aria-label', `Cart, ${cart.item_count} items`));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      return cart;
    } catch (err) { console.error(err); }
  }
  function openDrawer() {
    const d = $('[data-cart-drawer]'); if (!d) return false;
    d.hidden = false; document.body.style.overflow = 'hidden';
    $('.drawer__panel', d)?.focus();
    releaseDrawer = trap(d, closeDrawer); return true;
  }
  function closeDrawer() {
    const d = $('[data-cart-drawer]'); if (!d || d.hidden) return;
    d.hidden = true; document.body.style.overflow = '';
    if (releaseDrawer) { releaseDrawer(); releaseDrawer = null; }
  }
  document.addEventListener('click', e => {
    if (e.target.closest('[data-cart-open]') && drawerEnabled() && !document.body.classList.contains('template-cart')) { e.preventDefault(); openDrawer(); }
    if (e.target.closest('[data-cart-close]')) closeDrawer();
  });
  async function changeLine(key, quantity) {
    const d = $('[data-cart-drawer]'); d?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${R.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }) });
      const body = await res.json();
      if (!res.ok) toast(body.description || body.message || 'That quantity isn’t available.');
      await refreshDrawer(); announce('Cart updated');
      $('.drawer__panel', d)?.focus();
    } finally { d?.removeAttribute('aria-busy'); }
  }
  document.addEventListener('click', e => { const b = e.target.closest('[data-line-change]'); if (b) changeLine(b.dataset.lineChange, parseInt(b.dataset.qty, 10)); });
  document.addEventListener('change', e => { const i = e.target.closest('[data-line-input]'); if (i) changeLine(i.dataset.lineInput, parseInt(i.value, 10) || 0); });

  async function addToCart(formData, button) {
    const label = button ? button.innerHTML : '';
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      const res = await fetch(`${R.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const body = await res.json();
      if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart.');
      await refreshDrawer();
      announce(`${body.product_title || 'Item'} added to cart`);
      if (!openDrawer()) toast('Added to cart');
      return true;
    } catch (err) { toast(err.message); return false; }
    finally { if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.innerHTML = label; } }
  }
  document.addEventListener('submit', e => {
    const f = e.target.closest('[data-quick-add]');
    if (f && drawerEnabled()) { e.preventDefault(); addToCart(new FormData(f), $('button', f)); }
  });

  /* ---------- Product page ---------- */
  function initProduct(section) {
    const form = $('[data-product-form]', section); if (!form) return;
    const variants = JSON.parse($('[data-variants]', section)?.textContent || '[]');
    const idInput = $('[data-variant-id]', form), btn = $('[data-add-btn]', form), err = $('[data-form-error]', form);
    const qty = $('input[name=quantity]', form);
    const money = cents => { try { return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency: window.theme.currency || 'CAD' }).format(cents / 100); } catch { return (cents / 100).toFixed(2); } };
    $$('[data-qty-step]', form).forEach(b => b.addEventListener('click', () => { qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.qtyStep, 10)); }));
    const current = () => {
      const opts = $$('fieldset', form).map(fs => $('input:checked', fs)?.value).filter(v => v !== undefined);
      return variants.find(v => v.options.every((o, i) => o === opts[i]));
    };
    const showMedia = id => {
      const slides = $$('[data-gallery-slide]', section); if (!slides.length) return;
      let idx = slides.findIndex(s => s.dataset.mediaId === String(id)); if (idx < 0) return;
      goSlide(idx);
    };
    const goSlide = idx => {
      const slides = $$('[data-gallery-slide]', section);
      slides.forEach((s, i) => (s.hidden = i !== idx));
      $$('[data-gallery-thumb]', section).forEach((t, i) => t.setAttribute('aria-current', i === idx ? 'true' : 'false'));
      const c = $('[data-gallery-count]', section); if (c) c.textContent = `Photo ${idx + 1} of ${slides.length}`;
    };
    $$('[data-gallery-thumb]', section).forEach(t => t.addEventListener('click', () => goSlide(parseInt(t.dataset.galleryThumb, 10))));
    const curIdx = () => Math.max(0, $$('[data-gallery-slide]', section).findIndex(s => !s.hidden));
    const step = d => { const n = $$('[data-gallery-slide]', section).length; if (n) goSlide((curIdx() + d + n) % n); };
    $('[data-gallery-prev]', section)?.addEventListener('click', () => step(-1));
    $('[data-gallery-next]', section)?.addEventListener('click', () => step(1));
    const gm = $('.gallery__main', section); let sx = null;
    gm?.addEventListener('touchstart', e => { sx = e.touches[0].clientX; }, { passive: true });
    gm?.addEventListener('touchend', e => { if (sx === null) return; const dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1); sx = null; });
    form.addEventListener('change', e => {
      if (!e.target.matches('[data-option-input]')) return;
      const v = current();
      if (!v) { btn.disabled = true; btn.textContent = 'Unavailable'; return; }
      idInput.value = v.id;
      btn.disabled = !v.available; btn.textContent = v.available ? 'Add to cart' : 'Sold out';
      const priceEl = $('[data-price]', section);
      if (priceEl) priceEl.innerHTML = `<span class="price">${money(v.price)}${v.compare_at_price > v.price ? `<s>${money(v.compare_at_price)}</s>` : ''}</span>`;
      const sp = $('[data-sticky-price]'); if (sp) sp.textContent = money(v.price);
      const stock = $('[data-stock]', section); if (stock) { stock.textContent = v.available ? 'In stock' : 'Sold out'; stock.style.background = v.available ? 'var(--ok-bg)' : 'var(--bad-bg)'; stock.style.color = v.available ? 'var(--ok)' : 'var(--bad)'; }
      if (v.featured_media) showMedia(v.featured_media.id);
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState(null, '', url);
    });
    form.addEventListener('submit', e => {
      if (!drawerEnabled()) return;
      e.preventDefault(); if (err) err.hidden = true;
      addToCart(new FormData(form), btn).then(ok => { if (!ok && err) { err.hidden = false; err.textContent = 'This item couldn’t be added. Please try again.'; } });
    });
    $('[data-sticky-add]')?.addEventListener('click', () => btn.click());
  }
  $$('[data-product-section]').forEach(initProduct);

  /* ---------- Recommendations ---------- */
  $$('[data-recommendations]').forEach(async el => {
    if (el.children.length) return;
    try { const html = await (await fetch(el.dataset.url)).text(); const doc = new DOMParser().parseFromString(html, 'text/html'); const s = $('[data-recommendations]', doc); if (s) el.innerHTML = s.innerHTML; } catch {}
  });

  /* ---------- Collection filters ---------- */
  const filterForm = $('[data-filter-form]');
  if (filterForm) {
    let t;
    const apply = () => { const params = new URLSearchParams(new FormData(filterForm)); for (const [k, v] of [...params]) if (v === '') params.delete(k); location.search = params.toString(); };
    filterForm.addEventListener('change', e => { clearTimeout(t); t = setTimeout(apply, e.target.type === 'number' ? 700 : 50); });
  }
  $('[data-sort]')?.addEventListener('change', e => { const u = new URL(location.href); u.searchParams.set('sort_by', e.target.value); location.href = u; });
  $('[data-filters-toggle]')?.addEventListener('click', e => { const f = $('#CollectionFilters'); const o = f.classList.toggle('is-open'); e.currentTarget.setAttribute('aria-expanded', o); });

  /* ---------- Compare ---------- */
  const CMP = 'iot-compare';
  const cmpList = () => store.get(CMP, []);
  function drawCompareBar() {
    const bar = $('[data-compare-bar]'); const list = cmpList();
    $$('[data-compare]').forEach(c => (c.checked = list.some(x => x.handle === c.dataset.compare)));
    if (!bar) return;
    bar.hidden = list.length === 0;
    $('[data-compare-count]', bar).textContent = `Compare ${list.length} of 4`;
    $('[data-compare-chips]', bar).innerHTML = list.map(x => `<span class="chip">${esc(x.title)}</span>`).join('');
    const go = $('[data-compare-go]', bar); go.setAttribute('aria-disabled', list.length < 2 ? 'true' : 'false');
  }
  document.addEventListener('change', e => {
    const c = e.target.closest('[data-compare]'); if (!c) return;
    let list = cmpList();
    if (c.checked) {
      if (list.length >= 4) { c.checked = false; toast('You can compare up to four products.'); return; }
      list.push({ handle: c.dataset.compare, title: c.dataset.compareTitle });
    } else list = list.filter(x => x.handle !== c.dataset.compare);
    store.set(CMP, list); drawCompareBar();
  });
  document.addEventListener('click', e => {
    if (e.target.closest('[data-compare-clear]')) { store.set(CMP, []); drawCompareBar(); }
    const go = e.target.closest('[data-compare-go]'); if (go && go.getAttribute('aria-disabled') === 'true') { e.preventDefault(); toast('Choose at least two products to compare.'); }
  });
  drawCompareBar();

  const cmpPage = $('[data-compare-page]');
  if (cmpPage) (async () => {
    const box = $('[data-compare-root]', cmpPage); const list = cmpList();
    if (!list.length) { box.innerHTML = `<div class="empty"><h2>Nothing to compare yet</h2><p class="muted">${esc(cmpPage.dataset.emptyText)}</p><a class="btn btn--primary" href="${root}collections/all">Browse products</a></div>`; return; }
    const items = (await Promise.all(list.map(async x => {
      try { const res = await fetch(`${root}products/${encodeURIComponent(x.handle)}?section_id=compare-row`); if (!res.ok) return null; const doc = new DOMParser().parseFromString(await res.text(), 'text/html'); const el = $('[data-compare-item]', doc); if (!el) return null;
        return { ...el.dataset, specs: Object.fromEntries($$('[data-spec]', el).map(s => [s.dataset.spec, s.textContent.trim()])) };
      } catch { return null; }
    }))).filter(Boolean);
    const labels = [...new Set(items.flatMap(i => Object.keys(i.specs).filter(k => items.some(x => x.specs[k]))))];
    box.innerHTML = `<div class="cmp-table"><table><thead><tr><th scope="col" style="width:160px"><span class="visually-hidden">Feature</span></th>${items.map(i => `<th scope="col">${i.image ? `<img src="${esc(i.image)}" alt="">` : ''}<a href="${esc(i.url)}" style="display:block;margin-top:10px;font-weight:600">${esc(i.title)}</a></th>`).join('')}</tr></thead><tbody>
      <tr><th scope="row">Price</th>${items.map(i => `<td><span class="price" style="font-size:22px">${esc(i.price)}</span></td>`).join('')}</tr>
      <tr><th scope="row">Condition</th>${items.map(i => `<td>${i.condition ? `<span class="chip chip--${esc(i.condition.toLowerCase().replace(/\s+/g, '-'))}">${esc(i.condition)}</span>` : '—'}</td>`).join('')}</tr>
      <tr><th scope="row">Brand</th>${items.map(i => `<td>${esc(i.vendor || '—')}</td>`).join('')}</tr>
      ${labels.map(l => `<tr><th scope="row">${esc(l)}</th>${items.map(i => `<td>${esc(i.specs[l] || '—')}</td>`).join('')}</tr>`).join('')}
      <tr><th scope="row"><span class="visually-hidden">Actions</span></th>${items.map(i => `<td><div class="row">${i.available === 'true' ? `<form data-quick-add method="post" action="${esc(R.cartAdd)}"><input type="hidden" name="id" value="${esc(i.variant)}"><input type="hidden" name="quantity" value="1"><button class="btn btn--primary btn--sm" type="submit">Add to cart</button></form>` : '<span class="muted">Sold out</span>'}<button type="button" class="link-btn small" data-cmp-remove="${esc(i.handle)}">Remove</button></div></td>`).join('')}</tr>
    </tbody></table></div><div class="row"><a class="btn btn--secondary" href="${root}collections/all">Add more products</a><button type="button" class="btn btn--soft" data-cmp-clear-all>Clear comparison</button></div>`;
    box.addEventListener('click', e => {
      const r = e.target.closest('[data-cmp-remove]'); if (r) { store.set(CMP, cmpList().filter(x => x.handle !== r.dataset.cmpRemove)); location.reload(); }
      if (e.target.closest('[data-cmp-clear-all]')) { store.set(CMP, []); location.reload(); }
    });
  })();

  /* ---------- Form validation helpers ---------- */
  function fieldLabel(el) { return el.dataset.label || el.closest('label')?.childNodes[0]?.textContent?.trim() || el.name?.replace(/^contact\[|\]$/g, '') || 'This field'; }
  function setErr(el, msg) {
    const holder = el.closest('.field, .check, fieldset'); if (!holder) return;
    let e = holder.querySelector(':scope > .err[data-js-err]');
    if (!msg) { el.removeAttribute('aria-invalid'); if (e) e.remove(); return; }
    el.setAttribute('aria-invalid', 'true');
    if (!e) { e = document.createElement('span'); e.className = 'err'; e.dataset.jsErr = ''; holder.appendChild(e); }
    e.textContent = msg;
  }
  function validateScope(scope) {
    let firstBad = null;
    $$('input,select,textarea', scope).forEach(el => {
      const hid = el.closest('[hidden]'); if (el.type === 'hidden' || el.disabled || (hid && hid !== scope)) return;
      if (el.type === 'radio') return;
      let msg = '';
      const v = (el.value || '').trim();
      if (el.required && el.type === 'checkbox' && !el.checked) msg = `Please confirm ${fieldLabel(el)}.`;
      else if (el.required && el.type !== 'checkbox' && !v) msg = `${fieldLabel(el)} is required.`;
      else if (el.type === 'email' && v && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) msg = 'Enter a valid email address, like name@example.com.';
      else if (el.type === 'tel' && v && !/^[+()\-\s\d]{10,}$/.test(v)) msg = 'Enter a phone number with at least 10 digits.';
      else if (el.minLength > 0 && v && v.length < el.minLength) msg = `Please write at least ${el.minLength} characters.`;
      else if (el.type === 'number' && v && el.min !== '' && Number(v) < Number(el.min)) msg = `Enter ${el.min} or more.`;
      if (el.type === 'checkbox' && el.hasAttribute('data-multi')) return;
      setErr(el, msg); if (msg && !firstBad) firstBad = el;
    });
    $$('fieldset[data-required-group]', scope).forEach(fs => {
      const inputs = $$('input', fs).filter(i => i.type === 'radio' || i.type === 'checkbox');
      const ok = inputs.some(i => i.checked);
      const e = $('[data-err]', fs);
      if (e) { e.hidden = ok; e.textContent = ok ? '' : `Choose an option for ${fs.dataset.requiredGroup.toLowerCase()}.`; }
      if (!ok && !firstBad) firstBad = inputs[0];
    });
    if (firstBad) { firstBad.focus(); announce('Please fix the highlighted fields.'); return false; }
    return true;
  }
  $$('[data-simple-form]').forEach(f => f.addEventListener('submit', e => { if (!validateScope(f)) e.preventDefault(); }));
  $$('[data-focus-on-load]').forEach(el => { el.scrollIntoView({ block: 'center' }); el.focus({ preventScroll: true }); });

  /* ---------- Multi-step wizards (trade-in, repair booking) ---------- */
  function initWizard(wrap) {
    const form = $('[data-wizard-form]', wrap); if (!form || !$('[data-step]', form)) return;
    const steps = $$('[data-step]', form); const total = steps.length;
    const kind = wrap.dataset.wizard;
    let cur = 1, max = 1;
    const val = name => { const els = $$(`[name="contact[${name}]"]`, form); const el = els.find(x => (x.type !== 'radio' && x.type !== 'checkbox') || x.checked); return el ? (el.value || '').trim() : ''; };
    const syncMulti = () => { $$('[data-multi-out]', form).forEach(out => { out.value = $$(`[data-multi="${out.dataset.multiOut}"]:checked`, form).map(c => c.value).join(', '); }); };
    const sumText = keys => keys.split(',').map(k => val(k.trim())).filter(Boolean).join(', ') || '—';
    const refreshAside = () => {
      syncMulti();
      $$('[data-sum]', wrap).forEach(el => (el.textContent = sumText(el.dataset.sum)));
      const sp = $('[data-sum-price]', wrap); if (sp) sp.textContent = $('input[name="contact[Service]"]:checked', form)?.dataset.price || '—';
    };
    const buildReview = () => {
      syncMulti();
      const rows = []; const seen = new Set();
      steps.slice(0, -1).forEach((st, i) => {
        $$('input,select,textarea', st).forEach(el => {
          if (!el.name || !el.name.startsWith('contact[')) return;
          const key = el.name.slice(8, -1); if (seen.has(key)) return; seen.add(key);
          const v = val(key); if (!v) return; rows.push({ key, v, step: i + 1 });
        });
        $$('[data-multi-out]', st).forEach(out => { const key = out.dataset.multiOut; if (out.value) rows.push({ key, v: out.value, step: i + 1 }); });
      });
      const rv = $('[data-review]', form);
      if (rv) rv.innerHTML = rows.map(r => `<div><div><b>${esc(r.key)}</b><p>${esc(r.v)}</p></div><button type="button" class="link-btn small" data-goto="${r.step}">Edit</button></div>`).join('');
      const body = $('[data-summary-body]', form);
      if (body) body.value = rows.map(r => `${r.key}: ${r.v}`).join('\n');
      const bn = $('[data-backup-note]', form); if (bn) bn.hidden = !$('[data-backup-quote]:checked', form);
    };
    const show = (n, focus = true) => {
      cur = n; max = Math.max(max, n);
      steps.forEach(s => (s.hidden = +s.dataset.step !== n));
      $$('[data-progress] li', form).forEach((li, i) => { li.className = i + 1 < n ? 'is-done' : i + 1 === n ? 'is-current' : ''; const b = $('button', li); b.disabled = i + 1 > max; if (i + 1 === n) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current'); });
      $('[data-back]', form).hidden = n === 1;
      $('[data-next]', form).hidden = n === total;
      $('[data-submit]', form).hidden = n !== total;
      if (n === total) buildReview();
      if (kind === 'booking' && n === 3) drawCalendar();
      refreshAside();
      if (focus) { const h = $('h2,h3', steps[n - 1]); if (h) { h.focus({ preventScroll: true }); wrap.querySelector('.wiz').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }); } }
    };
    const stepValid = n => {
      const st = steps[n - 1];
      let ok = validateScope(st);
      if (kind === 'tradein' && n === 2) {
        const locks = $$('[data-multi="Locks"]:checked', form); const fs = $('[data-required-group="Locks"]', form); const e = $('[data-err]', fs);
        if (locks.some(l => l.hasAttribute('data-exclusive')) && locks.length > 1) { e.hidden = false; e.textContent = '“None of these” can’t be combined with other options.'; if (ok) locks[0].focus(); ok = false; }
      }
      if (kind === 'booking' && n === 3) {
        const d = $('[data-date-input]', form).value, t = $('[data-time-input]', form).value;
        $('[data-date-err]', form).hidden = !!d; $('[data-time-err]', form).hidden = !d || !!t;
        if (!d || !t) { ok = false; announce(!d ? 'Choose a date.' : 'Choose a time.'); }
      }
      return ok;
    };
    form.addEventListener('click', e => {
      if (e.target.closest('[data-next]')) { if (stepValid(cur)) show(cur + 1); }
      if (e.target.closest('[data-back]')) show(cur - 1);
      const g = e.target.closest('[data-goto]'); if (g && !g.disabled) { const n = +g.dataset.goto; if (n <= max) show(n); }
    });
    form.addEventListener('change', e => {
      const ex = e.target.closest('[data-exclusive]');
      if (ex && ex.checked) $$(`[data-multi="${ex.dataset.multi}"]`, form).forEach(c => { if (c !== ex) c.checked = false; });
      else if (e.target.matches('[data-multi]') && e.target.checked) $$(`[data-multi="${e.target.dataset.multi}"][data-exclusive]`, form).forEach(c => (c.checked = false));
      refreshAside();
    });
    form.addEventListener('input', refreshAside);
    form.addEventListener('keydown', e => { if (e.key === 'Enter' && e.target.matches('input') && cur < total) { e.preventDefault(); $('[data-next]', form).click(); } });
    form.addEventListener('submit', e => { buildReview(); if (!validateScope(steps[total - 1])) { e.preventDefault(); } });

    /* service cards pre-select */
    $$('[data-pick-service]', wrap).forEach(a => a.addEventListener('click', () => {
      const r = $$('input[name="contact[Service]"]', form).find(x => x.value === a.dataset.pickService);
      if (r) { r.checked = true; refreshAside(); show(2, false); setTimeout(() => $('h3', steps[1])?.focus({ preventScroll: true }), 400); }
    }));
    const hash = new URLSearchParams(location.search).get('service');
    if (hash) { const r = $$('input[name="contact[Service]"]', form).find(x => x.value.toLowerCase().includes(hash.toLowerCase())); if (r) { r.checked = true; max = 2; show(2, false); } }
    try { const pre = sessionStorage.getItem('iot-test-notes'); if (pre && kind === 'booking') { const ta = $('[data-issue]', form); if (ta && !ta.value) ta.value = pre; sessionStorage.removeItem('iot-test-notes'); const d = $$('input[name="contact[Service]"]', form)[0]; if (d && !$('input[name="contact[Service]"]:checked', form)) d.checked = true; max = 2; show(2, false); } } catch {}

    /* ----- booking calendar ----- */
    let viewMonth = null;
    function drawCalendar() {
      if (kind !== 'booking') return;
      const tz = wrap.dataset.tz || 'America/Toronto';
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date()).map(p => [p.type, p.value]));
      const today = `${parts.year}-${parts.month}-${parts.day}`; const nowMin = +parts.hour * 60 + +parts.minute;
      const ahead = +wrap.dataset.ahead || 42, lead = (+wrap.dataset.lead || 0) * 60, slotLen = +wrap.dataset.slot || 30;
      const addDays = (s, n) => { const d = new Date(s + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
      const last = addDays(today, ahead);
      const days = (wrap.dataset.days || '').split(',').map(s => s.trim().slice(0, 3).toLowerCase());
      const closed = (wrap.dataset.closed || '').split(/\s+/).filter(Boolean);
      const names = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const hoursFor = dow => { const h = dow === 6 ? wrap.dataset.saturdayHours : dow === 0 ? wrap.dataset.sundayHours : wrap.dataset.weekdayHours; const m = /(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/.exec(h || ''); return m ? [+m[1] * 60 + +m[2], +m[3] * 60 + +m[4]] : null; };
      const slotsFor = ds => {
        const dow = new Date(ds + 'T12:00:00Z').getUTCDay();
        if (!days.includes(names[dow]) || closed.includes(ds) || ds < today || ds > last) return [];
        const h = hoursFor(dow); if (!h) return [];
        const out = []; for (let m = h[0]; m + slotLen <= h[1]; m += slotLen) out.push({ m, ok: ds !== today || m >= nowMin + lead }); return out;
      };
      const fmtTime = m => { const h = Math.floor(m / 60), mm = m % 60; return `${((h + 11) % 12) + 1}:${String(mm).padStart(2, '0')} ${h < 12 ? 'a.m.' : 'p.m.'}`; };
      const fmtDate = s => new Date(s + 'T12:00:00Z').toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
      const dateIn = $('[data-date-input]', form), timeIn = $('[data-time-input]', form);
      const selDate = dateIn.dataset.iso || '';
      if (!viewMonth) viewMonth = (selDate || today).slice(0, 7);
      const [y, mo] = viewMonth.split('-').map(Number);
      const first = `${viewMonth}-01`; const startDow = new Date(first + 'T12:00:00Z').getUTCDay(); const nDays = new Date(Date.UTC(y, mo, 0)).getUTCDate();
      const title = new Date(first + 'T12:00:00Z').toLocaleDateString('en-CA', { month: 'long', year: 'numeric', timeZone: 'UTC' });
      const prevOk = viewMonth > today.slice(0, 7), nextOk = `${viewMonth}-${String(nDays).padStart(2, '0')}` < last;
      let cells = '';
      for (let i = 0; i < startDow; i++) cells += '<span></span>';
      for (let d = 1; d <= nDays; d++) { const ds = `${viewMonth}-${String(d).padStart(2, '0')}`; const ok = slotsFor(ds).some(s => s.ok); cells += `<button type="button" data-day="${ds}" ${ok ? '' : 'disabled'} aria-pressed="${selDate === ds}" aria-label="${fmtDate(ds)}${ok ? '' : ', unavailable'}">${d}</button>`; }
      $('[data-calendar]', form).innerHTML = `<div class="cal__head"><button type="button" data-month="-1" ${prevOk ? '' : 'disabled'} aria-label="Previous month">‹</button><b aria-live="polite">${title}</b><button type="button" data-month="1" ${nextOk ? '' : 'disabled'} aria-label="Next month">›</button></div><div class="cal__grid">${['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(x => `<span class="cal__dow" aria-hidden="true">${x}</span>`).join('')}${cells}</div>`;
      $('[data-date-label]', form).textContent = selDate ? fmtDate(selDate) : 'Choose a date first';
      $('[data-slots]', form).innerHTML = selDate ? slotsFor(selDate).map(s => `<button type="button" data-slot="${s.m}" ${s.ok ? '' : 'disabled'} aria-pressed="${timeIn.dataset.min === String(s.m)}">${fmtTime(s.m)}</button>`).join('') : '';
      form.__cal = { fmtDate, fmtTime, tz };
    }
    form.addEventListener('click', e => {
      const day = e.target.closest('[data-day]'), slot = e.target.closest('[data-slot]'), mon = e.target.closest('[data-month]');
      const dateIn = $('[data-date-input]', form), timeIn = $('[data-time-input]', form);
      if (day) { dateIn.dataset.iso = day.dataset.day; dateIn.value = form.__cal.fmtDate(day.dataset.day); timeIn.value = ''; timeIn.dataset.min = ''; drawCalendar(); $(`[data-day="${day.dataset.day}"]`, form)?.focus(); refreshAside(); announce(`${dateIn.value} selected. Choose a time.`); }
      if (slot) { timeIn.dataset.min = slot.dataset.slot; timeIn.value = `${form.__cal.fmtTime(+slot.dataset.slot)} (${form.__cal.tz})`; drawCalendar(); $(`[data-slot="${slot.dataset.slot}"]`, form)?.focus(); refreshAside(); announce(`${timeIn.value} selected`); }
      if (mon) { const [y, m] = viewMonth.split('-').map(Number); const d = new Date(Date.UTC(y, m - 1 + +mon.dataset.month, 1)); viewMonth = d.toISOString().slice(0, 7); drawCalendar(); $(`[data-month="${mon.dataset.month}"]`, form)?.focus(); }
    });
    show(cur, false);
  }
  $$('[data-wizard]').forEach(initWizard);

  /* expose for other scripts */
  window.IOT = { announce, toast, store, esc };
})();
